import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
  Divider,
  Box,
  CardActions,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import AuthContext from "../Context/AuthProvider";
import AddPaymentDialog from "./AddPaymentDialog";
import { getBillById, addPayment, removePayment } from "../API/BillingApi";
import { PaymentModes } from "../Utility/Constants";
import { numberToWords } from "../Utility/Utility";
import ConfirmationDialog from "../common/ConfirmationDialog";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import { convertUTCToTimeZoneDateString } from "../Utility/DateTimeUtility";


const PaymentTableSection = ({
  billId,
  showSnackbar,
  patientId,
  onPaymentDataChange,
  onTotalPaidChange,
}) => {
  const { auth } = useContext(AuthContext);
  const theme = useTheme();
  const isSmallTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [payments, setPayments] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const paymentOptions = Object.values(PaymentModes);
  const [newPayment, setNewPayment] = useState({
    amount: "",
    paymentMode: "",
    notes: "",
    receivedFrom: "",
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [paymentToDeleteId, setPaymentToDeleteId] = useState(null);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const result = await getBillById(auth, billId);
      if (result.isSuccess && result.data?.payments) {
        setPayments(result.data.payments);
      } else {
        const msg =
          typeof result.data === "string"
            ? result.data
            : result.data?.title || "Failed to fetch payments.";
        showSnackbar?.(msg, "error");
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
      showSnackbar?.("Error fetching payments.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (billId) fetchPayments();
  }, [billId, auth, showSnackbar]);

  const handleAddPayment = async () => {
    const { amount, paymentMode, notes, receivedFrom } = newPayment;
    if (!amount || !paymentMode) {
      showSnackbar?.(
        "Please enter amount and select a payment mode.",
        "warning"
      );
      return;
    }

    setIsSaving(true);
    try {
      const response = await addPayment(
        auth,
        patientId,
        auth.selectedFacility?.id,
        parseFloat(amount),
        paymentMode,
        notes,
        true,
        billId,
        false,
        receivedFrom
      );

      if (response.isSuccess) {
        setPayments((prev) => [...prev, response.data]);
        setOpenDialog(false);
        showSnackbar?.("Payment added successfully!", "success");
        setNewPayment({
          amount: "",
          paymentMode: "",
          notes: "",
          receivedFrom: "",
        });
        await fetchPayments();
        onPaymentDataChange?.();
      } else {
        const msg =
          typeof response.data === "string"
            ? response.data
            : response.data?.title || "Failed to add payment.";
        showSnackbar?.(msg, "error");
      }
    } catch (error) {
      console.error("Error adding payment:", error);
      showSnackbar?.("An error occurred while adding payment.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePayment = (paymentId) => {
    setPaymentToDeleteId(paymentId);
    setOpenDeleteDialog(true);
  };

  const confirmDeletePayment = async () => {
    if (!paymentToDeleteId) return;

    try {
      const response = await removePayment(auth, paymentToDeleteId);
      if (response.isSuccess) {
        showSnackbar("Payment removed successfully!", "success");
        await fetchPayments();
        onPaymentDataChange?.();
      } else {
        showSnackbar(response.data || "Failed to remove payment.", "error");
      }
    } catch (error) {
      console.error("Error removing payment:", error);
      showSnackbar("An error occurred while removing payment.", "error");
    } finally {
      setOpenDeleteDialog(false);
      setPaymentToDeleteId(null);
    }
  };

  const totalPaid = useMemo(
    () => payments.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [payments]
  );

  const currentTotalPaidFormatted = totalPaid.toFixed(2);
  const currentTotalPaidInWords = numberToWords(currentTotalPaidFormatted);

  useEffect(() => {
    onTotalPaidChange?.(parseFloat(currentTotalPaidFormatted));
  }, [currentTotalPaidFormatted, onTotalPaidChange]);

  const columns = [
    { field: "paymentMode", headerName: "Payment Mode", width: 150 },
    {
      field: "receivedFrom",
      headerName: "Received From",
      flex: 1,
      minWidth: 180,
      renderCell: (params) =>
        params.row.id === "TOTAL" ? (
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            {params.value}
          </Typography>
        ) : (
          params.value
        ),
    },
    {
  field: "amount",
  headerName: "Amount",
  width: 130,
  align: 'right',
  headerAlign: 'right',
  renderCell: (params) =>
    params.row.id === "TOTAL" ? (
      <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
        Rs {currentTotalPaidFormatted}
      </Typography>
    ) : (
      `Rs ${Number(params.row.amount).toFixed(2)}`
    ),
},
    { field: "createdByUserName", headerName: "Received By", width: 150 },
     {
    field: "paymentDateTime",
    headerName: "Payment Date",
    width: 180,
    renderCell: (params) =>
      params.value
        ? convertUTCToTimeZoneDateString(
            params.value,
            auth?.selectedFacility?.timeZone
          )
        : "",
  },
   
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) =>
        params.row.id === "TOTAL" ? null : (
          <Tooltip title="Remove" arrow>
            <IconButton onClick={() => handleDeletePayment(params.row.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
    },
  ];

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          borderStyle: "none",
          maxHeight: "calc(100vh - 200px)",
          overflowY: "auto",
        }}
      >
        <CardHeader
          subheader="Payments"
          subheaderTypographyProps={{ variant: "h6" }}
          sx={{ paddingY: "10px" }}
          action={
            <Tooltip title="New Payment" arrow>
              <IconButton
                color="secondary"
                size="small"
                onClick={() => setOpenDialog(true)}
              >
                <AddRoundedIcon />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent
          sx={{ height: isSmallTablet ? "auto" : "300px", paddingTop: "0px" }}
        >
          {isSmallTablet ? (
            // Card view for small screens
            <Box sx={{ p: 1 }}>
              {loading ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <Card
                    key={index}
                    variant="outlined"
                    sx={{ mb: 2, borderRadius: 2 }}
                  >
                    <CardContent>
                      <Typography variant="h6">Loading...</Typography>
                    </CardContent>
                  </Card>
                ))
              ) : payments.length === 0 ? (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ textAlign: "center", p: 2 }}
                >
                  No payments found.
                </Typography>
              ) : (
                payments.map((row) => (
                  <Card
                    key={row.id}
                    variant="outlined"
                    sx={{ mb: 1.5, borderRadius: 2 }}
                  >
                    <CardContent>
                      <Typography variant="h5">
                        <strong>Payment</strong>:{" "}
                        <strong>Rs {Number(row.amount).toFixed(2)}</strong>
                      </Typography>
                      <Divider sx={{ my: 0.5 }} />
                      <Typography variant="body2">
                        Mode: {row.paymentMode}
                      </Typography>
                      <Typography variant="body2">
                        Received From: {row.receivedFrom}
                      </Typography>
                      <Typography variant="body2">
                        Received By: {row.createdByUserName}
                      </Typography>
                      <Typography variant="body2">
  Date:{" "}
  {convertUTCToTimeZoneDateString(
    row.paymentDateTime,
    auth?.selectedFacility?.timeZone  // Add selectedFacility
  )}
</Typography>
                      {row.notes && (
                        <Typography variant="body2">
                          Notes: {row.notes}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions
                      sx={{ justifyContent: "flex-end", paddingTop: 0 }}
                    >
                      <Tooltip title="Remove" arrow>
                        <IconButton
                          onClick={() => handleDeletePayment(row.id)}
                          color="error"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                ))
              )}
            </Box>
          ) : (
            // DataGrid view for larger screens
            <DataGrid
              rows={payments}
              columns={columns}
              getRowId={(row) => row.id}
              disableColumnFilter
              disableColumnMenu
              disableColumnSorting
              sx={{
                height: "100%",
                "& .MuiTablePagination-toolbar": {
                  minHeight: "unset",
                  alignItems: "center",
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    margin: 0,
                  },
              }}
              initialState={{ density: "compact" }}
              loading={loading} // ✅ This shows skeleton rows
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              hideFooter={false}
              slots={{
                footer: () => (
                  <Box
                    sx={{
                      height: "60px",
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      px: 2,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {totalPaid === 0
                        ? "Total Payment Amount (INR Zero only)"
                        : `Total Payment Amount (INR ${currentTotalPaidInWords} only)`}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Rs {currentTotalPaidFormatted}
                    </Typography>
                  </Box>
                ),
              }}
            />
          )}
        </CardContent>
      </Card>

      <AddPaymentDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={handleAddPayment}
        newPayment={newPayment}
        setNewPayment={setNewPayment}
        paymentModes={paymentOptions}
        isSaving={isSaving}
        patientId={patientId}
      />

      <ConfirmationDialog
        title="Delete Payment"
        statement="Are you sure you want to delete this payment? This action cannot be undone."
          okButtonColor="error"
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDeletePayment}
      />
    </>
  );
};

export default PaymentTableSection;
